import { Injectable } from '@angular/core';
import { ServiceBase } from 'app/shared/serviceBase';
import { AuthenticationService } from 'app/shared/services/authentication-service';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { lastValueFrom, Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { CompanyAuditAddModel } from '../models/company-audit-add.model';
import { ICompanyAuditDetailsModel } from '../models/company-audit-details.model';
import { CompanyAuditModuleAddModel } from '../models/company-audit-module-add.model';
import { CompanyAuditModuleViewModel } from '../models/company-audit-module-view.model';
import { QuestionBankModel } from '../models/question-bank.model';
import { ModuleFormModel } from '../models/module-form.model';
import { DocumentModel } from '../models/document.model';
import * as FileSaver from 'file-saver';
import { ReportFiltersModel } from '../models/report-filters.model';
import { ReportCompanyAuditModuleModel } from '../models/report-company-audit-module';
import { ReportSelectorCompanies } from '../models/report-selector-companies.model';
import { ReportSelectorUsers } from '../models/report-selector-users.model';
import { ReportSelectorModules } from '../models/report-selector-modules.model';
import { RequestExternalAuditModel } from '../models/request-external-audit.model';
import { LenderModel } from '../models/lender.model';
import { CompanyAuditCommentAddModel } from '../models/company-audit-comment-add.model';
import { CompanyAuditCommentModel } from '../models/company-audit-comment.model';
import { CompanyAuditReportPeriodsViewModel } from '../models/company-audit-report-periods.model';

@Injectable()
export class CompanyAuditService extends ServiceBase {
    private commonUrl: string = this.url + '/CompanyAudit/';
    private reportUrl: string = this.url + '/Report/';
    constructor(
        private _http: HttpClient,
        private _authentication: AuthenticationService
    ) {
        super();
    }

    create(model: CompanyAuditAddModel): Observable<string> {
        const completeUrl: string = this.commonUrl;
        return this._http
            .post<string>(completeUrl, model).pipe(
                catchError(this.handleError));
    }

    update(model: ICompanyAuditDetailsModel): Observable<Response> {
        const completeUrl: string = this.commonUrl;
        return this._http
            .put(completeUrl, model).pipe(
                map((response: Response) => response),
                catchError(this.handleError));
    }

    getAll(companyId: string): Observable<ICompanyAuditDetailsModel[]> {
        const completeUrl: string = this.commonUrl + 'all/' + companyId;
        return this._http
            .get<ICompanyAuditDetailsModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    get(auditId: string): Observable<ICompanyAuditDetailsModel> {
        const completeUrl: string = this.commonUrl + auditId;
        return this._http
            .get<ICompanyAuditDetailsModel>(completeUrl).pipe(
                catchError(this.handleError));
    }

    closeAudit(audit: ICompanyAuditDetailsModel): Observable<boolean> {
        const completeUrl: string = this.commonUrl + 'close-audit';
        return this._http
            .post<boolean>(completeUrl, audit).pipe(
                catchError(this.handleError));
    }

    getAuditModules(auditId: string): Observable<CompanyAuditModuleViewModel[]> {
        const completeUrl: string = this.commonUrl + 'audit-modules/' + auditId;
        return this._http
            .get<CompanyAuditModuleViewModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getAuditModule(auditId: string, moduleId: string): Observable<CompanyAuditModuleViewModel> {
        const completeUrl: string = this.commonUrl + 'audit/' + auditId + '/' + moduleId;
        return this._http
            .get<CompanyAuditModuleViewModel>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    addModuleToAudit(model: CompanyAuditModuleAddModel): Observable<string> {
        const completeUrl: string = this.commonUrl + 'add-module';
        return this._http
            .post<string>(completeUrl, model).pipe(
                catchError(this.handleError));
    }

    deleteModuleFromAudit(auditId: string, moduleId: string): Observable<string> {
        const completeUrl: string = this.commonUrl + 'delete-module/' + auditId + '/' + moduleId;
        return this._http
            .delete<string>(completeUrl).pipe(
                catchError(this.handleError));
    }

    getQuestions(auditId: string, moduleId: string): Observable<QuestionBankModel[]> {
        const completeUrl: string = this.commonUrl + 'questions/' + auditId + '/' + moduleId;
        return this._http
            .get<QuestionBankModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getAnswers(auditId: string, moduleId: string): Observable<QuestionBankModel[]> {
        const completeUrl: string = this.commonUrl + 'answers/' + auditId + '/' + moduleId;
        return this._http
            .get<QuestionBankModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    updateAnswers(model: ModuleFormModel): Observable<string> {
        const completeUrl: string = this.commonUrl + 'update-answers';
        return this._http
            .post<string>(completeUrl, model).pipe(
                catchError(this.handleError));
    }

    upload(files: File[], model?: any): Observable<string> {
        const completeUrl: string = this.commonUrl + 'upload-document/' + model.moduleId;
        const formData: FormData = new FormData();
        files.forEach(file => {
            formData.append('file', file, file.name);
        });
        if (model) {
            formData.append('model', JSON.stringify(model));
        }
        return this._http.post<string>(completeUrl, formData).pipe(
            catchError(this.handleError));
    }

    getDocuments(companyAuditModuleId: string): Observable<DocumentModel[]> {
        const completeUrl: string = this.commonUrl + 'documents/' + companyAuditModuleId;
        return this._http
            .get<DocumentModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getDocument(id: string): Observable<HttpResponse<Blob>> {
        const completeUrl: string = this.commonUrl + 'document/' + id;
        return this._http.get(completeUrl, { responseType: 'blob', observe: 'response' })
            .pipe(catchError(this.handleError));
    }

    downloadGeneratedDocument(documentId: string): void {
        this.getDocument(documentId).subscribe(
            response => {
                const fileName = response.headers.get('content-disposition').split(';')[1].trim().split('=')[1].replace(/"/g, '');
                FileSaver.saveAs(response.body, fileName);
            }
        )
    }

    deleteDocument(id: string): Observable<Response> {
        const completeUrl: string = this.commonUrl + 'document/' + id;
        return this._http.delete(completeUrl).pipe(
            map((response: Response) => response),
            catchError(this.handleError));
    }

    reportCompanyAuditModules(model: ReportFiltersModel): Observable<ReportCompanyAuditModuleModel[]> {
        const completeUrl: string = this.commonUrl + 'report-company-audit-modules';
        return this._http
            .post<ReportCompanyAuditModuleModel[]>(completeUrl, model).pipe(
                catchError(this.handleError));
    }

    reportAvailableCompanies(): Observable<ReportSelectorCompanies[]> {
        const completeUrl: string = this.commonUrl + 'report-available-companies';
        return this._http.get<ReportSelectorCompanies[]>(completeUrl).pipe(
                catchError(this.handleError));
    }

    reportAvailableUsers(): Observable<ReportSelectorUsers[]> {
        const completeUrl: string = this.commonUrl + 'report-available-users';
        return this._http.get<ReportSelectorUsers[]>(completeUrl).pipe(
            catchError(this.handleError));
    }

    reportAvailableModules(): Observable<ReportSelectorModules[]> {
        const completeUrl: string = this.commonUrl + 'report-available-modules';
        return this._http.get<ReportSelectorModules[]>(completeUrl).pipe(
            catchError(this.handleError));
    }

    requestExternalAudit(model: RequestExternalAuditModel): Observable<RequestExternalAuditModel> {
        const completeUrl: string = this.commonUrl + 'request-external-audit';
        return this._http.post<RequestExternalAuditModel>(completeUrl, model).pipe(
            catchError(this.handleError));
    }

    getLenders(): Observable<LenderModel[]> {
        const completeUrl: string = this.commonUrl + 'lenders';
        return this._http
            .get<LenderModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    addCompanyAuditComment(model: CompanyAuditCommentAddModel): Observable<Response> {
        const completeUrl: string = this.commonUrl + 'add-company-audit-comment';
        return this._http
            .post(completeUrl, model).pipe(
                map((response: Response) => response),
                catchError(this.handleError));
    }

    getCompanyAuditComments(id: string): Observable<CompanyAuditCommentModel[]> {
        const completeUrl: string = this.commonUrl + 'company-audit-comments/' + id;
        return this._http
            .get<CompanyAuditCommentModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }

    getCompanyAuditReportPeriods(id: string): Observable<CompanyAuditReportPeriodsViewModel[]> {
        const completeUrl: string = this.commonUrl + 'company-audit-report-periods/' + id;
        return this._http
            .get<CompanyAuditReportPeriodsViewModel[]>(completeUrl)
            .pipe(catchError(this.handleError));
    }


  getReport(reportType: string, id: string): Observable<HttpResponse<Blob>> {
    const completeUrl: string = this.reportUrl + reportType + '-download/' + id;
    return this._http
      .get(completeUrl, { responseType: 'blob', observe: 'response' })
      .pipe(catchError(this.handleError));
  }

  async downloadReport(reportType: string, documentId: string): Promise<boolean> {
    return lastValueFrom(this.getReport(reportType, documentId)).then(
      response => {
        const fileName = response.headers.get('content-disposition').split(';')[1].trim().split('=')[1].replace(/"/g, '');
        FileSaver.saveAs(response.body, fileName);
        return true;
      }
    )
  }
}
